import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Collapse, styled } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import loadable from '@loadable/component';
import CaretDownRotate from '@components/UI/CaretDownRotate';
import CoreButton from '@components/Buttons/CoreButton';
import { useUserState } from '@context/UserContext';
import NavItem from './NavItem';
import { Feature, useFeature } from 'flagged';
import NavigationMenuPanel from '@domain/Header/components/Menus/NavigationMenuPanel';
import { NavLink } from 'react-router-dom';
import Currency from '@components/UI/Currency';
import useCartApi from '@hooks/API/useCartApi';
import useSelectedPlatform from '@hooks/useSelectedPlatform';
const Category = loadable(() => import('@components/Icons/Category'));
const StarOutlineIcon = loadable(() => import('@components/Icons/StarOutlineIcon'));
const Discovery = loadable(() => import('@components/Icons/Discovery'));
const Bag = loadable(() => import('@components/Icons/Bag'));
const Chat = loadable(() => import('@components/Icons/Chat'));
const Cart = loadable(() => import('@components/Icons/Cart'));
const ChevronRight = loadable(() => import('@components/Icons/ChevronRight'));
const Trophy = loadable(() => import('@components/UI/Icon/Icons/Trophy'));
const useStyles = makeStyles()((theme) => ({
    navigationMenuMobile: {
        width: '100%',
        position: 'relative',
        marginBottom: theme.spacing(6),
        '& h3': {
            fontFamily: theme.typography.fontFamily,
            fontSize: '12px',
            color: theme.palette.grey[500],
            padding: theme.spacing(2),
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.grey[100]}`,
        },
        '&>a': {
            listStyle: 'none',
            background: theme.palette.grey[50],
            display: 'flex',
            '& span': {
                fontSize: '12px',
            },
        },
        '&>a:nth-child(odd)': {
            listStyle: 'none',
            background: '#fff',
        },
        '& a, & button': {
            color: theme.palette.grey[400],
        },
    },
    icon: {
        minWidth: '0px',
        marginRight: theme.spacing(1),
        '& svg': {
            stroke: theme.palette.grey[400],
            width: '15px',
            fill: 'none',
        },
    },
    subIcon: {
        fontSize: '16px',
        minWidth: '0px',
        marginRight: theme.spacing(1.5),
    },
    subText: {
        border: '0px',
    },
    profileMenuWrapper: {
        width: '100%',
        flex: '1 1 auto',
        marginBottom: '1px',
        '& a, & button': {
            color: theme.palette.grey[300],
            minHeight: '50px',
            maxHeight: '50px',
            flexBasis: '50px',
            flex: '1 1 auto',
            border: '0px',
        },
    },
    otherMenuButton: {
        width: '100%',
        padding: `${theme.spacing(2.5, 2)} !important`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '50px !important',
        background: theme.palette.grey[50],
        border: '1px solid transparent',
        fontWeight: 600,
        '& > div': {
            display: 'flex',
            alignItems: 'center',
        },
    },
    categoryIcon: {
        stroke: theme.palette.grey[400],
        width: '15px',
        fill: 'none',
        marginRight: theme.spacing(1),
    },
    support: {
        fontSize: '12px',
        fontWeight: 600,
        width: '100%',
        margin: theme.spacing(0, 2),
        background: theme.palette.primary.light,
        color: `${theme.palette.primary.main} !important`,
        borderRadius: '31px',
        justifyContent: 'flex-start',
        '& svg': {
            fill: theme.palette.primary.main,
        },
    },
    supportButtonWrapper: {
        // A hack to prevent overflow
        display: 'flex',
        marginTop: theme.spacing(1.5),
    },
    supportChatIcon: {
        width: '17px',
        marginRight: theme.spacing(1),
    },
    supportChevronIcon: {
        width: '5px',
        marginLeft: 'auto',
    },
}));
const StyledCurrency = styled(Currency) `
  color: ${(props) => props.theme.palette.grey[900]};
`;
const EnhancedNavItem = styled(NavItem) `
  .MuiTypography-root {
    font-weight: 600;
    font-size: ${({ theme }) => theme.spacing(1.5)};
    color: ${({ theme }) => theme.palette.common.black};
  }
  .MuiListItemIcon-root {
    background-color: ${({ theme }) => theme.palette.grey[100]};
    padding: ${({ theme }) => theme.spacing(1)};
    border-radius: ${({ theme }) => theme.spacing(0.5)};
    margin-right: 0;
  }
  height: ${({ theme }) => theme.spacing(8)} !important;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[100]};
  background-color: ${({ theme }) => theme.palette.common.white};
  gap: ${({ theme }) => theme.spacing(2)};
`;
const CartIcon = styled(Cart) `
  background: ${({ theme }) => theme.palette.grey[100]};
  fill: none;
  stroke: ${({ theme }) => theme.palette.grey[400]};
  padding: 6px;
  border-radius: 4px;
`;
const MyCartIcon = styled(Cart) `
  stroke: ${({ theme }) => theme.palette.grey[400]};
  fill: none !important;
  width: 28px;
  margin-right: 8px !important;
`;
const MyCartNavLink = styled(NavLink) `
  display: flex;
  justify-content: space-between;

  & > span:first-of-type {
    display: flex;
  }
`;
const EnhancedNavigationMenuPanel = styled(NavigationMenuPanel) `
  & > button {
    border: none;
    padding: ${({ theme }) => theme.spacing(1, 2)};
    height: ${({ theme }) => theme.spacing(8)} !important;
    border-bottom: 1px solid ${({ theme }) => theme.palette.grey[100]};
    font-size: ${({ theme }) => theme.spacing(1.5)};
    gap: ${({ theme }) => theme.spacing(2)};
    div {
      margin-right: 0;
    }
  }
`;
const CollapseContainer = styled('div') `
  width: 100%;
  .MuiTypography-root {
    color: ${({ theme }) => theme.palette.grey[400]};
  }
  .MuiCollapse-root {
    width: 100%;
  }
`;
const OtherMenuItem = styled('button') `
  width: 100%;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1, 2)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${({ theme }) => theme.spacing(8)} !important;
  background: ${({ theme }) => theme.palette.common.white};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[100]};
  font-weight: 600;
  & > div {
    background-color: ${({ theme }) => theme.palette.grey[100]};
    padding: ${({ theme }) => theme.spacing(1)};
    border-radius: ${({ theme }) => theme.spacing(0.5)};
    display: flex;
    align-items: center;
  }
  & > span {
    align-items: flex-end;
    flex: 1;
    display: flex;
    font-size: ${({ theme }) => theme.spacing(1.5)};
  }
  .MuiListItemIcon-root svg {
    height: ${({ theme }) => theme.spacing(2)};
    fill: none;
    stroke: ${({ theme }) => theme.palette.grey[400]};
  }
`;
const TrophyNav = styled(EnhancedNavItem) `
  border-top: 1px solid ${({ theme }) => theme.palette.grey[100]};
  .MuiListItemIcon-root > svg {
    fill: ${({ theme }) => theme.palette.grey[400]};
    stroke-width: 0;
  }
`;
const SupportContainer = styled('div') `
  // A hack to prevent overflow
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  .support-button {
    display: flex;
    font-size: 12px;
    font-weight: 600;
    width: 100%;
    margin: ${({ theme }) => theme.spacing(0, 2)};
    background: ${({ theme }) => theme.palette.primary.light};
    color: ${({ theme }) => theme.palette.primary.main};
    border-radius: 31px;
    justify-content: flex-start;
    & svg {
      fill: ${({ theme }) => theme.palette.primary.main};
    }
  }
  .support-chat-icon {
    width: 17px;
    margin-right: ${({ theme }) => theme.spacing(1)};
  }
  .support-chevron-icon {
    width: 5px;
    margin-left: auto;
  }
`;
/**
 * This component is the menu navigation used in the mobile view sidebar of the app.
 *
 * @returns {React.ReactElement} Mobile Menu
 */
const NavigationMenuMobile = ({ onClose }) => {
    const { classes } = useStyles();
    const { authenticated, userProfile } = useUserState();
    const [showOtherMenu, setShowOtherMenu] = useState(false);
    const rewardsEnabled = !!useFeature('enable_rewards');
    const isCoinBonusesDisabled = !!useFeature('disable_coin_bonuses');
    const isWithdrawalOnlyEnabled = !!useFeature('semisonic_phase_2');
    const { quantity, totalPrice } = useCartApi();
    const { platformSlug } = useSelectedPlatform();
    const handleOtherClick = () => {
        setShowOtherMenu(!showOtherMenu);
    };
    const handleSupportClick = () => {
        var _a;
        if (window.zE) {
            const widget = ((_a = window.zE) === null || _a === void 0 ? void 0 : _a.widget) === 'messenger' ? 'messenger' : 'webWidget';
            window.zE(widget, 'show');
            window.zE(widget, 'open');
        }
        onClose();
    };
    const profileTournamentsLink = `/user/profile/${userProfile === null || userProfile === void 0 ? void 0 : userProfile.handle}/tournaments`;
    return (_jsxs(_Fragment, { children: [!isWithdrawalOnlyEnabled && (_jsxs(_Fragment, { children: [_jsx(TrophyNav, { to: `${platformSlug ? `/${platformSlug}` : ''}/compete`, icon: _jsx(Trophy, {}), linkText: "Compete", iconStyles: classes.icon }), _jsxs(EnhancedNavigationMenuPanel, { icon: _jsx(CartIcon, {}), label: "Marketplace", newBadge: true, children: [authenticated && (_jsx("li", { children: _jsxs(MyCartNavLink, { exact: true, to: "/marketplace/cart", activeClassName: "selected", onClick: onClose, children: [_jsxs("span", { children: [_jsx(MyCartIcon, {}), " My Cart ", _jsxs("span", { "data-ignore": true, children: ["(", quantity, ")"] })] }), _jsx("span", { children: _jsx(StyledCurrency, { amount: totalPrice || 0, currency: "PM" }) })] }) })), _jsx("li", { children: _jsx(NavLink, { exact: true, to: "/marketplace", activeClassName: "selected", onClick: onClose, children: "Overview" }) }), _jsx("li", { children: _jsx(NavLink, { exact: true, to: "/marketplace/gift-cards", activeClassName: "selected", onClick: onClose, children: "Gift Cards" }) }), _jsx(Feature, { name: "enable_cosmetics", children: _jsx(Feature, { name: "enable_marketplace_cosmetics", children: _jsx("li", { children: _jsx(NavLink, { exact: true, to: "/marketplace/cosmetics", activeClassName: "selected", onClick: onClose, children: "Cosmetics" }) }) }) }), rewardsEnabled && authenticated && (_jsx("li", { children: _jsx(NavLink, { exact: true, to: "/marketplace/claim-prizes", activeClassName: "selected", onClick: onClose, children: "Claim Prizes" }) }))] }), authenticated && (_jsxs(_Fragment, { children: [_jsx(EnhancedNavItem, { to: profileTournamentsLink, icon: _jsx(StarOutlineIcon, {}), linkText: "My Tournaments", iconStyles: classes.icon }), !isCoinBonusesDisabled && (_jsx(EnhancedNavItem, { to: "/coins", icon: _jsx(Bag, {}), linkText: "My Rewards", iconStyles: classes.icon }))] })), _jsx(EnhancedNavItem, { href: "/content", icon: _jsx(Discovery, {}), linkText: "News", iconStyles: classes.icon })] })), _jsxs(OtherMenuItem, { type: "button", onClick: handleOtherClick, children: [_jsx("div", { className: "MuiListItemIcon-root", children: _jsx(Category, {}) }), _jsx("span", { children: "Other" }), _jsx(CaretDownRotate, { rotate: showOtherMenu })] }), _jsx(CollapseContainer, { children: _jsx(Collapse, { in: showOtherMenu, children: _jsxs("div", { children: [!isWithdrawalOnlyEnabled && (_jsxs(_Fragment, { children: [_jsx(EnhancedNavItem, { href: "mailto:contact@repeat.gg?subject=Contact Repeat", linkText: "Brands" }), _jsx(EnhancedNavItem, { href: "mailto:contact@repeat.gg?subject=Contact Repeat", linkText: "Contact Us" })] })), _jsx(EnhancedNavItem, { to: "/about/terms-and-conditions", linkText: "Terms of Use" }), _jsx(EnhancedNavItem, { to: "/about/privacy-policy", linkText: "Privacy Policy" }), _jsx(EnhancedNavItem, { href: "https://support.repeat.gg/hc/en-us/requests/new", linkText: "Knowledge Base" })] }) }) }), _jsx(SupportContainer, { className: 'support-button-wrapper', children: _jsxs(CoreButton, { className: 'support-button', variant: "primary", onClick: handleSupportClick, children: [_jsx(Chat, { className: 'support-chat-icon' }), " Contact Support ", _jsx(ChevronRight, { className: 'support-chevron-icon' })] }) })] }));
};
export default NavigationMenuMobile;
